import { useMemo } from "react";
import { Stack, TextField, Typography } from "@mui/material";
import { useEntityRegistry } from "../../api/registry";
import type { ConditionDto, ConditionEntityHitsDto } from "../../api/types";
import { EntityPickerField } from "../EntityPickerField";
import type { PickerEntity } from "../EntityPickerField/entity";
import type { PickerKind } from "../EntityPickerField/config";
import { ENTITY_FIELD_KINDS, isPatternOperator } from "./constants";
import { useConditionEntityHits } from "./conditionEntityHits";

type FieldCondition = Extract<ConditionDto, { type: "field" }>;

export type ConditionValueFieldProps = {
  condition: FieldCondition;
  onChange: (c: ConditionDto) => void;
  /** Where this leaf sits in the tree, for looking up its entity hits. */
  path: number[];
};

/** `"a,b"` <-> `["a", "b"]` — how a multi-selection travels in the DTO. */
const idList = (value: string): string[] =>
  value ? value.split(",").filter(Boolean) : [];

const NAMES_SHOWN = 4;

const nameList = (names: string[]): string =>
  names.length > NAMES_SHOWN
    ? `${names.slice(0, NAMES_SHOWN).join(", ")} +${names.length - NAMES_SHOWN} more`
    : names.join(", ");

const plural = (kind: string, count: number) =>
  count === 1 ? kind : `${kind}s`;

/**
 * The entries the picker may show, hidden ones included.
 *
 * The picker's default list comes from the registry context, which drops
 * hidden entries — so a rule that names one would render a blank where its
 * selection should be, as if the pick had been lost. A rule flagged *because*
 * an entity was hidden has to be able to show it; the hit line underneath is
 * what says it can't run like that.
 */
const useEntityOptions = (kind: PickerKind | undefined): PickerEntity[] => {
  const { data } = useEntityRegistry(true);
  return useMemo(() => {
    const byKind: Partial<Record<PickerKind, object | undefined>> = {
      source: data?.sources,
      group: data?.groups,
      "expense-type": data?.expenseTypes,
    };
    const entries = kind ? byKind[kind] : undefined;
    // Same order the registry context puts them in, so this picker doesn't
    // list its entries differently from every other one in the app.
    const options = Object.values(entries ?? {}) as (PickerEntity & {
      priority?: number | null;
    })[];
    return options.sort(
      (a, b) =>
        (b.priority || 0) - (a.priority || 0) || a.name.localeCompare(b.name),
    );
  }, [kind, data]);
};

/**
 * What this leaf currently names in the registry, in one line under the box.
 *
 * A pattern that hits nothing is the case worth interrupting for: the rule
 * would be saved dead, and the backend would report it only afterwards. Hits
 * on entries that have been hidden are called out separately, because "it
 * matches, but only things that were removed" is a different fix.
 */
const HitLine = ({ hits }: { hits: ConditionEntityHitsDto }) => {
  const { kind, matched, hidden, error } = hits;

  const [text, warn] = error
    ? [error, true]
    : matched.length === 0 && hidden.length > 0
      ? [
          hidden.length === 1
            ? `Only matches ${kind} "${hidden[0]}", which is hidden — the rule won't run`
            : `Only matches hidden ${plural(kind, hidden.length)}: ${nameList(hidden)} — the rule won't run`,
          true,
        ]
      : matched.length === 0
        ? [`No ${kind} matches this`, true]
        : [
            `${matched.length} ${plural(kind, matched.length)} match: ${nameList(matched)}${
              hidden.length > 0
                ? ` (plus ${hidden.length} hidden: ${nameList(hidden)})`
                : ""
            }`,
            false,
          ];

  return (
    <Typography
      variant="caption"
      sx={{ color: warn ? "warning.main" : "text.secondary" }}
    >
      {text}
    </Typography>
  );
};

/**
 * The value input for one condition leaf, chosen by field and operator: an
 * entity picker where the field names a registry, a pair of boxes for the
 * range operators, plain text otherwise.
 */
export const ConditionValueField = ({
  condition,
  onChange,
  path,
}: ConditionValueFieldProps) => {
  const { field, operator, value } = condition;
  const kind = ENTITY_FIELD_KINDS[field];
  const hits = useConditionEntityHits(path);
  const options = useEntityOptions(kind);

  const picking = operator === "is" || operator === "isOneOf";

  // Picking from a list can't be a typo, so its hits are only worth the line
  // when something is wrong with them — a pattern's count is worth it always.
  // A picker with nothing chosen yet is not "wrong", it is unfinished: the
  // backend reads it as an operator error, which is not something to say to
  // someone who has just opened the box. Saving still names it.
  const showHits =
    hits !== undefined &&
    !(picking && !value) &&
    (isPatternOperator(operator) ||
      hits.error !== null ||
      hits.matched.length === 0 ||
      hits.hidden.length > 0);

  const input =
    kind && picking ? (
      operator === "isOneOf" ? (
        <EntityPickerField<string>
          kind={kind}
          multiple
          size="small"
          label=""
          includeHidden
          items={options}
          value={idList(value)}
          onChange={(ids) => onChange({ ...condition, value: ids.join(",") })}
        />
      ) : (
        <EntityPickerField<string>
          kind={kind}
          size="small"
          label=""
          includeHidden
          items={options}
          // Creating an entity here would name something no transaction
          // carries yet, which is never what a condition wants.
          allowCreate={false}
          value={value || null}
          onChange={(id) => onChange({ ...condition, value: id ?? "" })}
        />
      )
    ) : operator === "between" || operator === "betweenDays" ? (
      <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
        <TextField
          size="small"
          value={value.split(",")[0] || ""}
          onChange={(e) => {
            const max = value.split(",")[1] || "";
            onChange({ ...condition, value: `${e.target.value},${max}` });
          }}
          placeholder="Min"
          type="number"
          fullWidth
        />
        <TextField
          size="small"
          value={value.split(",")[1] || ""}
          onChange={(e) => {
            const min = value.split(",")[0] || "";
            onChange({ ...condition, value: `${min},${e.target.value}` });
          }}
          placeholder="Max"
          type="number"
          fullWidth
        />
      </Stack>
    ) : (
      <TextField
        size="small"
        value={value}
        onChange={(e) => onChange({ ...condition, value: e.target.value })}
        placeholder="Value"
        fullWidth
        type={field === "amount" || field === "datePaid" ? "number" : "text"}
      />
    );

  return (
    <Stack spacing={0.25} sx={{ width: "100%" }}>
      {input}
      {showHits && <HitLine hits={hits} />}
    </Stack>
  );
};
